import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { RiDownloadLine, RiFileCopyLine, RiFileExcelLine, RiFileTextLine, RiCheckLine } from '@remixicon/react';
import { toast } from 'sonner';
import type { QueryResult } from '@/lib/api';

interface ExportMenuProps {
  result: QueryResult;
  query: string;
}

export function ExportMenu({ result, query }: ExportMenuProps) {
  const [copied, setCopied] = useState(false);

  const exportToCSV = () => {
    if (!result.data.length) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(result.data[0]);
    const csvContent = [
      headers.join(','),
      ...result.data.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            if (value === null) return '';
            if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `query-results-${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Exported to CSV', {
      description: `${result.data.length} rows exported successfully`,
    });
  };

  const exportToJSON = () => {
    if (!result.data.length) {
      toast.error('No data to export');
      return;
    }

    const jsonContent = JSON.stringify(result.data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `query-results-${Date.now()}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Exported to JSON', {
      description: `${result.data.length} rows exported successfully`,
    });
  };

  const exportToSQL = () => {
    if (!result.data.length) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(result.data[0]);
    const sqlStatements = result.data
      .map((row) => {
        const values = headers
          .map((header) => {
            const value = row[header];
            if (value === null) return 'NULL';
            if (typeof value === 'string') return `'${value.replace(/'/g, "''")}'`;
            if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
            return value;
          })
          .join(', ');
        return `INSERT INTO table_name (${headers.join(', ')}) VALUES (${values});`;
      })
      .join('\n');

    const content = `-- Exported from Moniva\n-- Query: ${query}\n-- Rows: ${result.data.length}\n-- Exported at: ${new Date().toISOString()}\n\n${sqlStatements}`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `query-results-${Date.now()}.sql`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Exported to SQL', {
      description: `${result.data.length} INSERT statements generated`,
    });
  };

  const copyToClipboard = async () => {
    if (!result.data.length) {
      toast.error('No data to copy');
      return;
    }

    const headers = Object.keys(result.data[0]);
    const textContent = [
      headers.join('\t'),
      ...result.data.map((row) => headers.map((header) => row[header] ?? '').join('\t')),
    ].join('\n');

    try {
      await navigator.clipboard.writeText(textContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Copied to clipboard', {
        description: 'Data copied as tab-separated values',
      });
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const exportExcelCompatible = () => {
    if (!result.data.length) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(result.data[0]);
    const csvContent = [
      headers.join('\t'),
      ...result.data.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            if (value === null) return '';
            if (typeof value === 'string' && (value.includes('\t') || value.includes('"') || value.includes('\n'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join('\t')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/tab-separated-values;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `query-results-${Date.now()}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Exported for Excel', {
      description: 'File can be opened in Excel or Google Sheets',
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <RiDownloadLine className="h-4 w-4" />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs">Export Options</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={copyToClipboard} className="gap-2 cursor-pointer">
          {copied ? (
            <RiCheckLine className="h-4 w-4 text-green-600" />
          ) : (
            <RiFileCopyLine className="h-4 w-4" />
          )}
          <div className="flex-1">
            <p className="text-sm font-medium">Copy to Clipboard</p>
            <p className="text-xs text-muted-foreground">Tab-separated values</p>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onClick={exportToCSV} className="gap-2 cursor-pointer">
          <RiFileTextLine className="h-4 w-4" />
          <div className="flex-1">
            <p className="text-sm font-medium">Export as CSV</p>
            <p className="text-xs text-muted-foreground">Comma-separated values</p>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={exportToJSON} className="gap-2 cursor-pointer">
          <RiFileTextLine className="h-4 w-4" />
          <div className="flex-1">
            <p className="text-sm font-medium">Export as JSON</p>
            <p className="text-xs text-muted-foreground">JavaScript object notation</p>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={exportExcelCompatible} className="gap-2 cursor-pointer">
          <RiFileExcelLine className="h-4 w-4 text-green-600" />
          <div className="flex-1">
            <p className="text-sm font-medium">Export for Excel</p>
            <p className="text-xs text-muted-foreground">Open in Excel/Sheets</p>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem onClick={exportToSQL} className="gap-2 cursor-pointer">
          <RiFileTextLine className="h-4 w-4" />
          <div className="flex-1">
            <p className="text-sm font-medium">Export as SQL</p>
            <p className="text-xs text-muted-foreground">INSERT statements</p>
          </div>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <div className="px-2 py-1.5">
          <p className="text-xs text-muted-foreground">
            {result.rowCount} {result.rowCount === 1 ? 'row' : 'rows'} • {result.executionTime}ms
          </p>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
